import {Spec} from 'immutability-helper';
import $ from 'jquery';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import NoteDAO from 'notebook/bioentities/NoteDAO';
import NoteStore from 'notebook/bioentities/NoteStore';
import type {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {CidIdMap, CidNotePartProvenanceIdMap} from 'notebook/notes/NoteConstants';
import {MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {FromServerNote, BioentityServerNoteVersion, Note} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';

const NoteActions = {
  print: (fileId: string, param: {showTimestamps: boolean}): void => {
    const state = NoteStore.getEditorState(fileId)!;
    const noteId = state.cidIdMap[state.note.cid];
    let paramString = '';
    Object.entries(param).forEach((key, value) => {
      paramString += `&${key}=${value}`;
    });
    window.open(`view?note=${noteId}${paramString}#print`, '_blank');
  },

  registerObjects: (fileId: string, cidIdMap: CidIdMap): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_REGISTER_OBJECTS,
      fileId,
      cidIdMap,
    });
  },

  load: (fileId: string, note: FromServerNote): JQueryPromise<Array<BioentityServerNoteVersion>> => {
    // Merge in the changes into the open protocol, client-side.
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_LOAD,
      fileId,
      note,
    });
    return NoteActions.fetchVersions(fileId);
  },

  close: (fileId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_CLOSE,
      fileId,
    });
  },

  update: (
    fileId: string,
    op: Spec<Note>,
    {bumpVersion = true, multidocTableOp}: {bumpVersion?: boolean; multidocTableOp?: MultidocTableOpMap} = {}
  ): void => {
    // Merge in the changes into the open protocol, client-side.
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_UPDATE,
      fileId,
      op,
      cidIdMap: {},
      bumpVersion,
      multidocTableOp,
    });
  },

  replace: (fileId: string, note: Note, restoreData: SelectionRestoreData): void => {
    // Replace the open protocol with the specified data.
    // Useful to reset the state to an older state.
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_REPLACE,
      fileId,
      note,
      restoreData,
    });
  },

  loadVersion: (fileId: string, versionId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_LOAD_VERSION,
      fileId,
      versionId,
    });
    NoteDAO.getVersion(fileId, versionId)
      .done((response) => {
        assert(response != null, 'NoteDAO.getVersion unexpectedly returned undefined');
        AppDispatcher.dispatch({
          actionType: EventConstants.NOTE_LOAD_VERSION_COMPLETED,
          fileId,
          versionId,
          version: response,
        });
      })
      .fail(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.NOTE_LOAD_VERSION_FAILED,
          fileId,
          versionId,
        });
      });
  },

  fetchVersions: (fileId: string): JQueryPromise<Array<BioentityServerNoteVersion>> => {
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_FETCH_VERSIONS,
      fileId,
    });
    return NoteDAO.getVersions(fileId)
      .done((response) => {
        assert(response != null, 'NoteDAO.getVersions unexpectedly returned undefined');
        AppDispatcher.dispatch({
          actionType: EventConstants.NOTE_FETCH_VERSIONS_COMPLETED,
          fileId,
          versions: response,
        });
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.NOTE_FETCH_VERSIONS_FAILED,
          fileId,
        });
      });
  },

  save: (fileId: string): JQueryPromise<boolean> => {
    // Saves the open note to the server.

    // This is saved here in case the note is removed from store before save finishes.
    // TODO(T1847): remove after adding refcounts
    let {note, openVersion} = NoteStore.getEditorState(fileId)!;
    let serverNote = NoteStore.prepareForSave(fileId, note);

    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();
    benchlingUtil.asyncThrottle(`note-${fileId}`, () => {
      if (NoteStore.inStore(fileId)) {
        ({note, openVersion} = NoteStore.getEditorState(fileId)!);
        // Avoid doing a save if the note is already up-to-date.
        if (!NoteStore.hasUnsavedChanges(fileId)) {
          return deferredPromise.resolve(false).promise();
        }

        // Only send the mutable fields
        serverNote = NoteStore.prepareForSave(fileId, note);
      }

      AppDispatcher.dispatch({
        actionType: EventConstants.NOTE_SAVE,
        fileId,
      });
      const xhr = NoteDAO.update(fileId, serverNote)
        .done((response: {cidIdMap: CidIdMap; cidNotePartProvenanceIdMap?: CidNotePartProvenanceIdMap}) => {
          AppDispatcher.dispatch({
            actionType: EventConstants.NOTE_SAVE_COMPLETED,
            fileId,
            savedVersion: openVersion,
            versionId: response.cidIdMap[serverNote.cid],
            cidIdMap: response.cidIdMap,
            cidNotePartProvenanceIdMap: response.cidNotePartProvenanceIdMap,
          });
          if (NoteStore.inStore(fileId)) {
            NoteActions.fetchVersions(fileId);
          }
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        })
        .fail((response) => {
          notifications.stickyNotifyForFriendlyError(response, 'note-save-fail');
          AppDispatcher.dispatch({
            actionType: EventConstants.NOTE_SAVE_FAILED,
            fileId,
            error: response,
          });
          // Resolve the deferred promise to signal that the save has failed.
          deferredPromise.resolve(false);
        });
      return xhr;
    });
    // Return the deferred promise so that the caller can wait for the save to complete.
    return deferredPromise.promise();
  },
};

export default NoteActions;
